import Facade from '@/Facade'
import { IBuyBonusRequest, IGambleRequest, ISpinRequest } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { URLParser } from '../URLParser'
import { Model } from '@/model/Model'
import { md5 } from '@/utils/Md5'
import { SpinResponseParser } from './SpinResponseParser'
import { OPERATOR_CONFIG } from '@modules/src/operatorConfig'
import { getGameEnginePath } from '@/utils/GameEnginePath'

export class ServerRequests {
    private readonly _urlParser: URLParser
    private readonly _address: string
    private readonly _apiEndpoint: string

    private _lastAction: string
    private _sessionToken: string
    private _request: XMLHttpRequest
    private _model: Model
    private _timestamp: string
    private _lastSpinCoinValue: number
    private _normalizer: SpinResponseParser

    public constructor(model: Model) {
        this._urlParser = new URLParser()
        this._model = model
        this._address = this.resolveServerAddress()
        this._apiEndpoint = this.resolveApiEndpoint()
        this._timestamp = ''
        this._lastSpinCoinValue = 0
        this._normalizer = new SpinResponseParser(model)
        console.log('[ServerRequests] Using backend address:', this._address)
        console.log('[ServerRequests] Using API endpoint:', this._apiEndpoint)
        this.init()
        Facade.requestOngoing = false
    }

    private isLocalHostRuntime(): boolean {
        const hostValue = (window.location.hostname || '').toLowerCase()
        return hostValue === 'localhost' || hostValue === '127.0.0.1'
    }

    private isViteDevRuntime(): boolean {
        return Boolean((import.meta as any)?.env?.DEV)
    }

    private isLocalProxyRuntime(): boolean {
        // In local/dev all gameengine calls must go through Vite proxy.
        return this.isViteDevRuntime() || this.isLocalHostRuntime()
    }

    private resolveGameengineBaseUrl(): string {
        const search = new URLSearchParams(window.location.search)

        if (this.isLocalProxyRuntime()) {
            return ''
        }

        const explicitApiBase =
            search.get('api_url') ||
            search.get('backend_url') ||
            (this._urlParser.urlData.extParams && this._urlParser.urlData.extParams.api_url) ||
            (this._urlParser.urlData.extParams && this._urlParser.urlData.extParams.backend_url)

        if (explicitApiBase) {
            return explicitApiBase.endsWith('/') ? explicitApiBase.slice(0, -1) : explicitApiBase
        }

        return getGameEnginePath()
    }

    private resolveConfiguredOperatorEndpoint(
        key: 'serverPromoURL' | 'getBalanceURL' | 'cancelPFSURL' | 'tournamentsLeaderboardURL',
        fallbackPhpFileName: string
    ): string {
        const configuredValue = String(OPERATOR_CONFIG?.defaults?.[key] || '').trim()
        if (!configuredValue) {
            return this.resolveGameenginePhpEndpoint(fallbackPhpFileName)
        }

        if (/^https?:\/\//i.test(configuredValue)) {
            return configuredValue
        }

        const base = this.resolveGameengineBaseUrl()

        if (configuredValue.startsWith('/')) {
            return base ? `${base}${configuredValue}` : configuredValue
        }

        if (configuredValue.endsWith('.php')) {
            return this.resolveGameenginePhpEndpoint(configuredValue)
        }

        return this.resolveGameenginePhpEndpoint(fallbackPhpFileName)
    }

    private resolveGameenginePhpEndpoint(phpFileName: string): string {
        const normalizedPhpFileName = String(phpFileName || '')
            .trim()
            .replace(/^\/+/, '')
        const base = this.resolveGameengineBaseUrl()
        const relativePath = `/gameengine/casino/${normalizedPhpFileName}`
        return base ? `${base}${relativePath}` : relativePath
    }

    private resolveApiEndpoint(): string {
        if (this.isLocalProxyRuntime()) {
            return '/gameengine/casino/casino.php'
        }

        const search = new URLSearchParams(window.location.search)
        const explicitApiEndpoint =
            search.get('api_endpoint') ||
            (this._urlParser.urlData.extParams && this._urlParser.urlData.extParams.api_endpoint)

        if (explicitApiEndpoint) {
            return explicitApiEndpoint
        }

        const explicitApiBase =
            search.get('api_url') ||
            search.get('backend_url') ||
            (this._urlParser.urlData.extParams && this._urlParser.urlData.extParams.api_url) ||
            (this._urlParser.urlData.extParams && this._urlParser.urlData.extParams.backend_url)

        if (explicitApiBase) {
            const normalizedBase = explicitApiBase.endsWith('/') ? explicitApiBase.slice(0, -1) : explicitApiBase
            return `${normalizedBase}/gameengine/casino/casino.php`
        }

        const apiBase = getGameEnginePath()
        return apiBase ? `${apiBase}/gameengine/casino/casino.php` : '/gameengine/casino/casino.php'
    }

    private resolveServerAddress(): string {
        if (this.isLocalProxyRuntime()) {
            return '/casino/slots/'
        }

        const search = new URLSearchParams(window.location.search)

        const explicitAddress =
            search.get('backend_url') ||
            search.get('api_url') ||
            (this._urlParser.urlData.extParams && this._urlParser.urlData.extParams.backend_url) ||
            (this._urlParser.urlData.extParams && this._urlParser.urlData.extParams.api_url)

        if (explicitAddress) {
            return explicitAddress.endsWith('/') ? explicitAddress : `${explicitAddress}/`
        }

        const baseHost = getGameEnginePath()

        const backendPath =
            search.get('backend_path') ||
            (this._urlParser.urlData.extParams && this._urlParser.urlData.extParams.backend_path) ||
            '/casino/slots/'

        const normalizedPath = backendPath.startsWith('/') ? backendPath : `/${backendPath}`
        const suffix = normalizedPath.endsWith('/') ? normalizedPath : `${normalizedPath}/`
        return baseHost ? `${baseHost}${suffix}` : suffix
    }

    private resolveGameId(params: URLSearchParams): string {
        return Facade.gameId
    }

    public init() {
        this._request = new XMLHttpRequest()
        this._request.addEventListener('error', this.onError.bind(this))
        this._request.addEventListener('load', this.onGameLoad.bind(this))
    }

    public getInit() {
        if (Facade.requestOngoing) return
        this._lastAction = 'init'
        this.sendInitRequestType1()
    }

    private sendInitRequestType1() {
        const params = new URLSearchParams(window.location.search)

        const gameId = this.resolveGameId(params)
        const amountType = params.get('amount_type') || '1'
        const username = params.get('username') || this._urlParser.urlData.playerUid || ''
        const sessionId = params.get('session_id') || this._urlParser.urlData.casinoToken || ''
        const platformType = this.getPlatformType(params)

        const payload = new URLSearchParams({
            game_id: gameId,
            request_type: '1',
            amount_type: amountType,
            username,
            session_id: sessionId,
            platform_type: platformType,
            round_code: this.generateRoundCode(),
        })

        console.log('[ServerRequests] Init payload:', payload.toString())

        this.sendFormEncoded(payload.toString())
    }

    private sendFormEncoded(payload: string) {
        if (this._request.readyState !== 0 && this._request.readyState !== 4) {
            this._request.abort()
        }

        this._request.open('POST', this._apiEndpoint, true)
        this._request.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
        this._request.send(payload)
        Facade.requestOngoing = true
    }

    private generateRoundCode(): string {
        const ts = this._timestamp || Date.now().toString()
        const nonce = `${Date.now()}${Math.floor(Math.random() * 1000000)}`
        const privateKey = 'pzvlxpvrctiypcpbrstpcpxcyadhrg'
        const str = ts + privateKey + nonce.slice(-6)
        return md5(str)
    }

    private getPlatformType(params: URLSearchParams): 'desktop' | 'mobile' {
        const explicit = (params.get('platform_type') || '').toLowerCase()
        if (explicit === 'desktop' || explicit === 'mobile') return explicit

        const channel = (params.get('channel') || '').toLowerCase()
        if (channel.includes('mobile') || channel === 'm') return 'mobile'

        const hasMobileUA = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent)
        return hasMobileUA ? 'mobile' : 'desktop'
    }

    private resolveNonEmptyString(values: Array<string | null | undefined>, fallback: string): string {
        for (const value of values) {
            if (typeof value === 'string' && value.trim().length > 0) {
                return value
            }
        }

        return fallback
    }

    private resolveSpinAmountType(params: URLSearchParams): string {
        const spinData: any = this._model.spinData || {}

        if (this._model.isPromoSpinsRound()) {
            return this.resolveNonEmptyString([spinData?.amount_type, spinData?.promo_details?.amount_type, '3'], '3')
        }

        return this.resolveNonEmptyString([params.get('amount_type')], '1')
    }

    public getSpin(data: ISpinRequest) {
        if (Facade.requestOngoing) return
        Facade.status = null

        this._lastAction = 'spin'
        const numericRequestType = Number(data.requestType)
        const requestType =
            Number.isFinite(numericRequestType) && numericRequestType > 0 ? String(Math.trunc(numericRequestType)) : '2'

        const params = new URLSearchParams(window.location.search)
        const gameId = this.resolveGameId(params)
        const amountType = this.resolveSpinAmountType(params)
        const username = params.get('username') || this._urlParser.urlData.playerUid || ''
        const sessionId = params.get('session_id') || this._urlParser.urlData.casinoToken || ''
        const platformType = this.getPlatformType(params)

        const initData: any = this._model.initData || {}
        const promoDetails = (this._model.spinData as any)?.promo_details
        const promoCoinValue = Number(promoDetails?.coin_value)
        const promoNumCoins = Number(promoDetails?.num_coins)
        const usePromoStake = this._model.isPromoSpinsRound()
        const numCoins =
            usePromoStake && Number.isFinite(promoNumCoins) && promoNumCoins > 0
                ? promoNumCoins
                : Number(initData?.game?.num_coins || initData?.game?.fixed_coins || 1)
        const coinValue =
            usePromoStake && Number.isFinite(promoCoinValue) && promoCoinValue > 0 ? promoCoinValue : data.coinValue
        const rawNumBetlines = initData?.game?.num_paylines ?? initData?.game?.num_betlines ?? 1
        const numBetlines = Number(rawNumBetlines)

        const payload = new URLSearchParams({
            game_id: gameId,
            request_type: requestType,
            coin_value: String(coinValue),
            num_coins: String(Number.isFinite(numCoins) ? numCoins : 1),
            num_betlines: String(Number.isFinite(numBetlines) && numBetlines > 0 ? numBetlines : 1),
            amount_type: amountType,
            username,
            session_id: sessionId,
            platform_type: platformType,
            round_code: this.generateRoundCode(),
        })

        if (requestType === '6') {
            payload.set('pick_position', String(Number.isFinite(data.pickPosition as number) ? data.pickPosition : 0))
            payload.set(
                'bonus_game_id',
                String(Number.isFinite(data.bonusGameId as number) ? data.bonusGameId : Number(`${gameId}003`))
            )
        }

        console.log('[ServerRequests] Spin payload:', payload.toString())
        this._lastSpinCoinValue = Number(coinValue)

        this.sendFormEncoded(payload.toString())

        window.force_grids = null
    }

    public getBuyBonus(data: IBuyBonusRequest) {
        if (Facade.requestOngoing) return

        let requestData = {
            game_code: this._urlParser.urlData.gameCode,
            session_token: data.sessionToken,
            amount: data.amount,
            type: data.type,
            currency: this._urlParser.urlData.currency,
            coin_value: data.coinValue,
            buy_freebet_id: data.buy_freebet_id,
        }
        this.sendMessage('bet', requestData)
        this._lastAction = 'buyBonus'
    }

    public getGamble(data: IGambleRequest) {
        if (Facade.requestOngoing) return

        this._lastAction = 'gamble'

        const params = new URLSearchParams(window.location.search)
        const gameId = this.resolveGameId(params)
        const amountType = params.get('amount_type') || '1'
        const username = params.get('username') || this._urlParser.urlData.playerUid || ''
        const sessionId = params.get('session_id') || this._urlParser.urlData.casinoToken || ''
        const platformType = this.getPlatformType(params)

        const payload = new URLSearchParams({
            game_id: gameId,
            request_type: '4',
            amount_type: amountType,
            username,
            session_id: sessionId,
            platform_type: platformType,
            pick_position: String(Number.isFinite(data.position) ? data.position : 3),
            bet_amount: String(Number.isFinite(data.betAmount) ? data.betAmount : 0),
            round_code: this.generateRoundCode(),
        })

        console.log('[ServerRequests] Gamble payload:', payload.toString())
        this.sendFormEncoded(payload.toString())
    }

    public getTerminate() {
        navigator.sendBeacon(
            this._address + 'terminate',
            JSON.stringify({
                game_code: this._urlParser.urlData.gameCode,
                session_token: this._sessionToken,
                type: 'terminate',
            })
        )
    }

    public cancelPromoFreeSpins(bfsRecordId?: number | string) {
        try {
            this._lastAction = 'cancelPFS'

            const params = new URLSearchParams(window.location.search)
            const gameId = this.resolveGameId(params)
            const username = params.get('username') || this._urlParser.urlData.playerUid || ''
            const sessionId = params.get('session_id') || this._urlParser.urlData.casinoToken || ''
            const platformType = this.getPlatformType(params)

            // Recupera account_id dai parametri URL o dall'initData
            const initData: any = this._model.initData || {}
            const accountId = params.get('account_id') || initData?.player?.account_id || initData?.account_id || ''

            console.log('[ServerRequests] Cancel PFS - Parameters:', {
                gameId,
                username,
                sessionId,
                platformType,
                accountId,
                bfsRecordId,
            })

            // amount_type deve essere 1 quando si declina, perché la campagna non è stata accettata
            // amount_type=3 viene usato solo DOPO aver accettato e durante i promo spins
            const amountType = '1'

            const payload = new URLSearchParams({
                game_id: gameId,
                request_type: '8',
                amount_type: amountType,
                username,
                bfs_record_id: bfsRecordId == null ? '' : String(bfsRecordId),
                account_id: accountId,
                session_id: sessionId,
                platform_type: platformType,
                round_code: this.generateRoundCode(),
            })

            // Costruisci l'URL per cancel_pfs.php (non usa casino.php)
            const cancelPfsEndpoint = this.resolveCancelPfsEndpoint()

            console.log('[ServerRequests] Cancel PFS endpoint:', cancelPfsEndpoint)
            console.log('[ServerRequests] Cancel PFS payload:', payload.toString())

            this.sendFormEncodedToEndpoint(payload.toString(), cancelPfsEndpoint)
        } catch (error) {
            console.error('[ServerRequests] Error in cancelPromoFreeSpins:', error)
            Facade.requestOngoing = false
            throw error
        }
    }

    public claimPromoFreeSpins(bfsRecordId: number | string) {
        try {
            this._lastAction = 'claimPFS'

            const params = new URLSearchParams(window.location.search)
            const gameId = this.resolveGameId(params)
            const username = params.get('username') || this._urlParser.urlData.playerUid || ''
            const sessionId = params.get('session_id') || this._urlParser.urlData.casinoToken || ''
            const platformType = this.getPlatformType(params)
            const initData: any = this._model.initData || {}
            const accountId = params.get('account_id') || initData?.player?.account_id || initData?.account_id || ''

            const payload = new URLSearchParams({
                game_id: gameId,
                request_type: '1',
                amount_type: '3',
                username,
                bfs_record_id: String(bfsRecordId),
                account_id: accountId,
                session_id: sessionId,
                platform_type: platformType,
                round_code: this.generateRoundCode(),
            })

            const claimEndpoint = this.resolveConfiguredOperatorEndpoint('serverPromoURL', 'claim_pfs_bonus.php')
            this.sendFormEncodedToEndpoint(payload.toString(), claimEndpoint)
        } catch (error) {
            console.error('[ServerRequests] Error in claimPromoFreeSpins:', error)
            Facade.requestOngoing = false
            ;(GameSignals as any).promoFreeSpinsClaimFailed?.emit({ error })
        }
    }

    private resolveCancelPfsEndpoint(): string {
        return this.resolveConfiguredOperatorEndpoint('cancelPFSURL', 'cancel_pfs.php')
    }

    private sendFormEncodedToEndpoint(payload: string, endpoint: string) {
        try {
            if (this._request.readyState !== 0 && this._request.readyState !== 4) {
                this._request.abort()
            }

            console.log('[ServerRequests] Sending request to endpoint:', endpoint)
            console.log('[ServerRequests] Payload:', payload)

            this._request.open('POST', endpoint, true)
            this._request.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
            this._request.send(payload)
            Facade.requestOngoing = true
        } catch (error) {
            console.error('[ServerRequests] Error sending request to endpoint:', endpoint, error)
            Facade.requestOngoing = false
            throw error
        }
    }

    public getBalance() {
        if (Facade.requestOngoing) return
        this._lastAction = 'balance'
        this.sendMessage('balance', {
            casino_token: this._urlParser.urlData.casinoToken,
            session_token: this._sessionToken,
            type: 'balance',
        })
    }

    public sendMessage(action: string, data: any) {
        if (this._request.readyState !== 0 && this._request.readyState !== 4) {
            if (action === 'init' || action === 'bet') {
                this._request.abort()
            }
        }
        if (!Facade.requestOngoing) {
            this._request.open('POST', this._address + action, true)
            this._request.setRequestHeader('Content-Type', 'application/json')
            this._request.send(JSON.stringify(data))
            Facade.requestOngoing = true
        }
    }

    private onError(event: any) {
        // `errorConnection` is also owned by the framework SharedPopupManager, which would race the
        // game's localized popup and could render a second English dialog. Keep transport failures
        // on the game-owned common-error path; the same recovery listeners subscribe to it.
        GameSignals.commonError.emit()

        GameSignals.errorMessage.emit({
            params: { message: 'Connection error' },
            errorCode: event.target.status,
            errorMessage: { msg: 'Connection Error' },
        })

        console.log('ERROR', event)
    }

    private onGameLoad(event: any) {
        const data = JSON.parse(event.target.response)
        console.log('SERVER RESPONSE: ', data)

        if (this._lastAction === 'init' && data && data.game && data.game.timestamp) {
            this._timestamp = String(data.game.timestamp)
        }

        if (data.hasOwnProperty('error') || data.error_number || data.error_code) {
            if (data.error && data.error.name === 'stimeout') {
                GameSignals.sessionTimeout.emit(data)
            } else {
                GameSignals.casinoError.emit(data)
            }

            const statusCode = Number(data.status)
            const fallbackCode = Number(data.error_number)
            const resolvedCode = Number.isFinite(statusCode)
                ? statusCode > 1000
                    ? -Math.abs(statusCode - 1000)
                    : statusCode
                : Number.isFinite(fallbackCode)
                  ? -Math.abs(fallbackCode)
                  : -1

            const resolvedMessage = data.message || data.error_message || data.error_code || 'Connection Error'

            GameSignals.errorMessage.emit({
                params: { message: 'Connection error' },
                errorCode: resolvedCode,
                errorMessage: { msg: resolvedMessage },
            })

            Facade.requestOngoing = false

            return
        }

        if (data.status && data.status == 911) {
            Facade.requestOngoing = false
            Facade.status = 911
            GameSignals.realityCheck.emit(data)
            return
        }

        switch (this._lastAction) {
            case 'init':
                this.onInitLoad(data)
                break
            case 'spin':
                this.onSpinLoad(data)
                break
            case 'balance':
                this.onBalanceLoad(data)
                break
            case 'buyBonus':
                this.onBuyBonusLoad(data)
                break
            case 'gamble':
                this.onGambleLoad(data)
                break
            case 'cancelPFS':
                this.onCancelPFSLoad(data)
                break
            case 'claimPFS':
                this.onClaimPFSLoad(data)
                break
            case 'tournament':
            case 'tournamentJoin':
                this.onTournamentLoad(data)
                break
        }
        Facade.requestOngoing = false
    }

    private onInitLoad(data: any) {
        this._sessionToken = data.session_token
        GameSignals.backendMessage.emit({ action: 'init', data: this.normalizeGamblePayloadAmounts(data) })
    }

    private onSpinLoad(data: any) {
        const normalizedGambleOffer = this.normalizeGamblePayloadAmounts(data)

        if (String(data?.next_round?.type || '').toLowerCase() === 'gamble') {
            ;(GameSignals as any).backendMessage.emit({ action: 'gambleOffer', data: normalizedGambleOffer })
        }

        const spinData = this._normalizer.parse(data, this._sessionToken, this._lastSpinCoinValue)
        GameSignals.backendMessage.emit({ action: 'spin', data: spinData })
    }

    private onBalanceLoad(data: any) {
        this._sessionToken = data.session_token
        GameSignals.backendMessage.emit({ action: 'balance', data: data })
    }

    private onBuyBonusLoad(data: any) {
        GameSignals.backendMessage.emit({ action: 'buyBonus', data: data })
    }

    private onCancelPFSLoad(data: any) {
        try {
            console.log('[ServerRequests] Cancel PFS response:', data)

            // Controlla se ci sono errori specifici nella risposta
            if (data.error || data.error_number || data.error_code || data.error_message) {
                console.error('[ServerRequests] Cancel PFS error received:', {
                    error: data.error,
                    error_number: data.error_number,
                    error_code: data.error_code,
                    error_message: data.error_message,
                    full_data: data,
                })
            }

            GameSignals.backendMessage.emit({ action: 'cancelPFS', data })
        } catch (error) {
            console.error('[ServerRequests] Error processing Cancel PFS response:', error, data)
            // Emetti comunque il segnale anche in caso di errore
            GameSignals.backendMessage.emit({ action: 'cancelPFS', data })
        }
    }

    private onClaimPFSLoad(data: any) {
        GameSignals.backendMessage.emit({ action: 'claimPFS', data })
    }

    private onTournamentLoad(data: any) {
        try {
            console.log('[ServerRequests] Tournament response:', data)
            GameSignals.backendMessage.emit({ action: 'tournament', data })
        } catch (error) {
            console.error('[ServerRequests] Error processing tournament response:', error, data)
        }
    }

    private onGambleLoad(data: any) {
        GameSignals.backendMessage.emit({ action: 'gamble', data: this.normalizeGamblePayloadAmounts(data) })
    }

    private normalizeGamblePayloadAmounts(data: any): any {
        if (!data || typeof data !== 'object') {
            return data
        }

        const cloned = this.clonePayload(data)

        if (cloned?.current_round && typeof cloned.current_round === 'object') {
            cloned.current_round.win_amount = this.normalizeMinorOrCurrencyAmount(
                cloned.current_round.win_amount,
                cloned.current_round.win_amount_currency
            )
        }

        if (cloned?.next_round && typeof cloned.next_round === 'object') {
            cloned.next_round.bet_amount = this.normalizeMinorOrCurrencyAmount(
                cloned.next_round.bet_amount,
                cloned.next_round.bet_amount_currency
            )
        }

        return cloned
    }

    private normalizeMinorOrCurrencyAmount(amountValue: any, amountCurrencyValue: any): number {
        const explicitCurrency = Number(amountCurrencyValue)
        if (Number.isFinite(explicitCurrency) && explicitCurrency >= 0) {
            return explicitCurrency
        }

        const amount = Number(amountValue)
        if (!Number.isFinite(amount) || amount < 0) {
            return 0
        }

        const coinValue = Number(this._lastSpinCoinValue)
        if (Number.isFinite(coinValue) && coinValue > 0 && coinValue < 1) {
            return amount * coinValue
        }

        // When coinValue is an index-like value (e.g. 1, 2, 3), gamble amounts are still in cents.
        return amount / 100
    }

    private clonePayload<T>(payload: T): T {
        if (typeof globalThis.structuredClone === 'function') {
            return globalThis.structuredClone(payload)
        }

        return JSON.parse(JSON.stringify(payload)) as T
    }

    // ============ TOURNAMENT METHODS ============

    private buildTournamentPayload(tournamentId: number, extra?: Record<string, string>): URLSearchParams {
        const params = new URLSearchParams(window.location.search)
        const sessionId = params.get('session_id') || this._urlParser.urlData.casinoToken || ''
        const platformType = this.getPlatformType(params)

        const initData: any = this._model.initData || {}
        const accountId = params.get('account_id') || initData?.player?.account_id || initData?.account_id || ''

        return new URLSearchParams({
            tounramentId: String(tournamentId), // typo nel vecchio framework
            account_id: String(accountId),
            session_id: sessionId,
            platform_type: platformType,
            ...(extra || {}),
        })
    }

    /**
     * The periodic leaderboard refresh, deliberately off the shared XMLHttpRequest.
     *
     * That one is serialized by Facade.requestOngoing, and getSpin returns without doing anything
     * while the flag is set, so a poll running through it would swallow any spin pressed during its
     * round trip — once per interval, for as long as the player is in the game. This request also
     * carries no game state, so it has nothing to gain from that queue.
     */
    public async refreshTournamentLeaderboardInBackground(tournamentId: number): Promise<void> {
        try {
            const response = await fetch(this.resolveTournamentEndpoint(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: this.buildTournamentPayload(tournamentId).toString(),
            })

            if (!response.ok) {
                console.warn('[ServerRequests] Tournament leaderboard poll rejected:', response.status)
                return
            }

            this.onTournamentLoad(JSON.parse(await response.text()))
        } catch (error) {
            // A dropped refresh is not worth surfacing: the next tick carries the same data.
            console.warn('[ServerRequests] Tournament leaderboard poll failed:', error)
        }
    }

    public getTournamentLeaderboard(tournamentId: number) {
        try {
            if (Facade.requestOngoing) return
            this._lastAction = 'tournament'

            const payload = this.buildTournamentPayload(tournamentId)
            const endpoint = this.resolveTournamentEndpoint()

            console.log('[ServerRequests] Tournament leaderboard request:', {
                tournamentId,
                endpoint,
                payload: payload.toString(),
            })

            this.sendFormEncodedToEndpoint(payload.toString(), endpoint)
        } catch (error) {
            console.error('[ServerRequests] Error in getTournamentLeaderboard:', error)
            Facade.requestOngoing = false
        }
    }

    public joinTournament(tournamentId: number) {
        try {
            if (Facade.requestOngoing) {
                // Prioritize explicit user Join action over in-flight leaderboard refresh.
                if (this._lastAction === 'tournament') {
                    console.log('[ServerRequests] Aborting in-flight leaderboard request to send join request')
                    try {
                        this._request.abort()
                    } catch (abortError) {
                        console.warn('[ServerRequests] Unable to abort in-flight request before join:', abortError)
                    }
                    Facade.requestOngoing = false
                } else {
                    console.warn('[ServerRequests] Join tournament skipped because another request is in progress')
                    return
                }
            }
            this._lastAction = 'tournamentJoin'

            const payload = this.buildTournamentPayload(tournamentId, { playerJoined: '1' })
            const endpoint = this.resolveTournamentEndpoint()

            console.log('[ServerRequests] Tournament join request:', {
                tournamentId,
                endpoint,
                payload: payload.toString(),
            })

            this.sendFormEncodedToEndpoint(payload.toString(), endpoint)
        } catch (error) {
            console.error('[ServerRequests] Error in joinTournament:', error)
            Facade.requestOngoing = false
        }
    }

    private resolveTournamentEndpoint(): string {
        return this.resolveConfiguredOperatorEndpoint('tournamentsLeaderboardURL', 'tournaments_leaderboard.php')
    }
}
